import React, { useRef, useState, useLayoutEffect, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { ConfirmationModal } from "./ConfirmationModal";
import { iconStatusMap, statusToGroup } from "./DefaultImports";
const ICON_COLOR_VARIATION_STORAGE_KEY = "reservation_icon_color_variation";
const ICON_COLOR_VARIATION_CHANGE_EVENT = "reservation_icon_color_variation_change";
const CANCELLATION_STATUSES = new Set(["cancelled_customer", "cancelled_restaurant", "cancelled", "Cancelled_By_Customer", "Cancelled_By_Restaurant", "Cancelled", "Cancelled_By_Customer_Warning"]);
const CUSTOMER_NOTIFICATION_STATUSES = new Set(["accepted", "cancelled_customer", "cancelled_restaurant", "cancelled", "Cancelled_By_Customer", "Cancelled_By_Restaurant", "Cancelled", "Cancelled_By_Customer_Warning"]);
const getStoredIconColorVariation = () => {
  if (typeof window === "undefined") return 2;
  const storedValue = window.localStorage.getItem(ICON_COLOR_VARIATION_STORAGE_KEY);
  if (storedValue === "1") return 1;
  if (storedValue === "2") return 2;
  return 2;
};
const isCancellationStatus = status => {
  if (!status) return false;
  return CANCELLATION_STATUSES.has(status);
};
const isCustomerNotificationStatus = status => {
  if (!status) return false;
  return CUSTOMER_NOTIFICATION_STATUSES.has(status);
};
const DropdownTrigger = styled.div`
  width: 100%;
  min-height: 30px;
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: ${({
  color
}) => color || "#99b0ad"};
  font-weight: 300;
  font-family: "Arial Black", "Segoe UI", sans-serif;
  cursor: pointer;
  padding: 0px 4px;
  box-sizing: border-box;
  overflow: hidden;
`;
const TextWrapper = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-weight: 300;
  font-family: "Arial Black", "Segoe UI", sans-serif;
  gap: 6px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  flex-wrap: nowrap;
  overflow: hidden;
`;
const WaitingListNumber = styled.span`
  flex: 0 0 auto;
  font-size: clamp(12px, 6.5cqw, 15px);
  line-height: 1.2;
  color: inherit;
`;
const LabelBox = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 100%;
  container-type: inline-size;
`;
const TextLabel = styled.span`
  font-size: clamp(12px, 6cqw, 15px);
  max-width: 100%;
  white-space: normal;
  overflow: hidden;
  display: block;
  line-height: 1.25;

  && {
    color: ${({
  $color
}) => $color || "inherit"} !important;
  }

  && * {
    color: inherit !important;
  }

  strong {
    font-weight: 900;
    font-size: clamp(12px, 6.5cqw, 15px);
    color: inherit !important;
  }
`;
const ChevronIcon = styled(FontAwesomeIcon)`
  flex: 0 0 auto;
  color: inherit;
  padding-right: 10px;
`;
const PortalDropdown = styled.div`
  position: absolute;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  min-width: 180px;
  z-index: 3000;
  padding: 4px 0;
  max-height: calc(100vh - 16px);
  overflow-y: auto;
`;
const DropdownItem = styled.div`
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  background-color: ${({
  $active
}) => $active ? "#ee754c" : "white"};
  color: ${({
  $active,
  $statusColor
}) => $active ? "white" : $statusColor || "#99b0ad"};
  font-weight: ${({
  $active
}) => $active ? "bold" : "normal"};

  &:hover {
    background-color: ${({
  $active
}) => $active ? "#ee754c" : "#f0f0f0"};
  }
`;
const ModalBodyContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const NotificationCheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid rgba(153, 176, 173, 0.45);
  border-radius: 8px;
  background: #faf6e9;
  cursor: pointer;
  user-select: none;
`;
const NotificationCheckbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 1px;
  accent-color: #ee754c;
  cursor: pointer;
  flex: 0 0 auto;
`;
const NotificationCheckboxText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-family: "Montserrat", "Segoe UI", sans-serif;
  color: #2f3f3c;
  font-size: 14px;
  line-height: 1.25;

  strong {
    color: #2f3f3c;
    font-weight: 700;
  }

  small {
    color: #6f7f7c;
    font-size: 12px;
    line-height: 1.25;
  }
`;
const StatusDropdown = ({
  reservation,
  allowedStatuses,
  onSelectStatus,
  getStatusColor,
  getStatusLabel
}) => {
  const {
    t
  } = useTranslation();
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [sendCustomerNotification, setSendCustomerNotification] = useState(true);
  const [selectedColorVariation, setSelectedColorVariation] = useState(getStoredIconColorVariation);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0
  });
  const isPendingCancellationStatus = isCancellationStatus(pendingStatus);
  const isPendingCustomerNotificationStatus = isCustomerNotificationStatus(pendingStatus);
  const getGroupedStatus = status => {
    return statusToGroup?.[status] || status;
  };
  const getGroupedStatusColor = status => {
    const group = getGroupedStatus(status);
    const statusConfig = iconStatusMap?.[group];
    const variationColor = statusConfig?.[`color_variation_${selectedColorVariation}`]?.trim?.();
    if (variationColor) return variationColor;
    const fallbackIconColor = statusConfig?.color?.trim?.();
    if (fallbackIconColor) return fallbackIconColor;
    if (typeof getStatusColor === "function") {
      return getStatusColor(status);
    }
    return "#99b0ad";
  };
  const currentStatusColor = getGroupedStatusColor(reservation.status);
  useEffect(() => {
    const updateColorVariation = event => {
      if (event?.type === "storage" && event.key !== ICON_COLOR_VARIATION_STORAGE_KEY) {
        return;
      }
      if (event?.type === ICON_COLOR_VARIATION_CHANGE_EVENT && (event.detail === 1 || event.detail === 2)) {
        setSelectedColorVariation(event.detail);
        return;
      }
      setSelectedColorVariation(getStoredIconColorVariation());
    };
    window.addEventListener("storage", updateColorVariation);
    window.addEventListener(ICON_COLOR_VARIATION_CHANGE_EVENT, updateColorVariation);
    return () => {
      window.removeEventListener("storage", updateColorVariation);
      window.removeEventListener(ICON_COLOR_VARIATION_CHANGE_EVENT, updateColorVariation);
    };
  }, []);
  useLayoutEffect(() => {
    if (visible && wrapperRef.current && dropdownRef.current) {
      const triggerRect = wrapperRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      const margin = 4;
      let top = triggerRect.bottom + window.scrollY;
      let left = triggerRect.left + window.scrollX;
      if (left + dropdownRect.width > viewportW + window.scrollX) {
        left = viewportW - dropdownRect.width - margin;
      }
      if (left < margin) {
        left = margin;
      }
      if (top + dropdownRect.height > viewportH + window.scrollY - margin) {
        top = triggerRect.top - dropdownRect.height + window.scrollY;
        if (top < window.scrollY + margin) {
          top = window.scrollY + margin;
        }
      }
      setCoords({
        top,
        left
      });
    }
  }, [visible]);
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setVisible(false);
      }
    };
    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visible]);
  const handleDropdownItemClick = status => {
    if (status !== reservation.status) {
      setPendingStatus(status);
      setSendCustomerNotification(true);
      setShowModal(true);
    }
  };
  const handleConfirm = () => {
    if (isCustomerNotificationStatus(pendingStatus)) {
      onSelectStatus(pendingStatus, {
        sendNotification: sendCustomerNotification
      });
    } else {
      onSelectStatus(pendingStatus);
    }
    setShowModal(false);
    setVisible(false);
    setPendingStatus(null);
    setSendCustomerNotification(true);
  };
  const handleCancel = () => {
    setShowModal(false);
    setPendingStatus(null);
    setSendCustomerNotification(true);
  };
  const handleToggleDropdown = () => {
    setSelectedColorVariation(getStoredIconColorVariation());
    setVisible(prev => !prev);
  };
  const notificationHelpText = isPendingCancellationStatus ? "Desativa esta opção se não quiseres enviar uma notificação sobre este cancelamento." : "Desativa esta opção se não quiseres enviar uma notificação ao cliente sobre a confirmação da reserva.";
  return <>
      <DropdownTrigger ref={wrapperRef} onClick={handleToggleDropdown} color={currentStatusColor}>
        <TextWrapper>
          {reservation.waiting_list_number != null && <WaitingListNumber>
              {reservation.waiting_list_number}º
            </WaitingListNumber>}

          <LabelBox>
            <TextLabel $color={currentStatusColor}>
              {getStatusLabel(reservation.status)}
            </TextLabel>
          </LabelBox>

          <ChevronIcon icon={visible ? faChevronUp : faChevronDown} size="xl" />
        </TextWrapper>
      </DropdownTrigger>

      {visible && createPortal(<PortalDropdown ref={dropdownRef} style={{
      top: `${coords.top}px`,
      left: `${coords.left}px`
    }}>
            {(allowedStatuses || []).map(status => {
        const statusColor = getGroupedStatusColor(status);
        const isActive = status === reservation.status;
        return <DropdownItem key={status} $active={isActive} $statusColor={statusColor} style={{
          pointerEvents: isActive ? "none" : "auto",
          opacity: isActive ? 0.6 : 1
        }} onClick={() => {
          handleDropdownItemClick(status);
          setVisible(false);
        }}>
                  {getStatusLabel(status)}
                </DropdownItem>;
      })}
          </PortalDropdown>, document.body)}

      <ConfirmationModal show={showModal} onHide={handleCancel} onConfirm={handleConfirm} title={t("ui.components.statusDropdown.alterarEstado")} body={<ModalBodyContent>
            <div>{t("ui.components.statusDropdown.tensACertezaQuePretendesAlterar")}{" "}
              <strong>{getStatusLabel(pendingStatus)}</strong>?
            </div>

            {isPendingCustomerNotificationStatus && <NotificationCheckboxRow>
                <NotificationCheckbox type="checkbox" checked={sendCustomerNotification} onChange={event => setSendCustomerNotification(event.target.checked)} />

                <NotificationCheckboxText>
                  <strong>{t("ui.components.statusDropdown.enviarNotificacao")}</strong>
                  <small>{notificationHelpText}</small>
                </NotificationCheckboxText>
              </NotificationCheckboxRow>}
          </ModalBodyContent>} confirmLabel="Alterar estado" cancelLabel="Cancelar" confirmColor="#EE754C" cancelColor="#99B0AD" />
    </>;
};
export default StatusDropdown;
